/**
 * AppDialog — shared, opinionated overlay primitives used across the app.
 *
 * Goals:
 *  - Mobile: full-height, sticky header (Back + ×) + sticky action footer
 *  - Desktop: centered modal with a sticky × pill that stays over scrolling content
 *  - Consistent radius, padding, hero gradient, and CTA gradient via tokens
 *  - Optional <AppDialogHero> banner with image + gradient + badges slot
 *
 * Compose like:
 *   <AppDialog open={open} onClose={close} size="xl">
 *     <AppDialogHero image={url} badges={...} />
 *     <AppDialogHeader eyebrow="Community" title="…" description="…" />
 *     <AppDialogBody>...</AppDialogBody>
 *     <AppDialogFooter>
 *       <Button>Primary</Button>
 *     </AppDialogFooter>
 *   </AppDialog>
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type Size = "sm" | "md" | "lg" | "xl" | "2xl";

const sizeMap: Record<Size, string> = {
  sm: "md:max-w-sm",
  md: "md:max-w-md",
  lg: "md:max-w-2xl",
  xl: "md:max-w-4xl",
  "2xl": "md:max-w-5xl",
};

interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  size?: Size;
  /** Render as a bottom sheet on mobile instead of fading from center. */
  mobileSheet?: boolean;
  children: React.ReactNode;
  className?: string;
  /** Provide for screen readers when no <AppDialogHeader> is used. */
  ariaTitle?: string;
}

export function AppDialog({
  open,
  onClose,
  size = "lg",
  mobileSheet = false,
  children,
  className,
  ariaTitle,
}: AppDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "gd-scrim fixed inset-0 z-[999998] backdrop-blur-sm motion-reduce:animate-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          aria-label={ariaTitle}
          className={cn(
            // Base + animations
            "gd-dialog fixed z-[999999] bg-background outline-none motion-reduce:animate-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Mobile full-screen vs bottom sheet
            mobileSheet
              ? "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
              : "inset-0 h-[100dvh] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // Desktop: centered with width
            "md:left-[50%] md:right-auto md:top-[50%] md:bottom-auto",
            "md:h-auto md:max-h-[92vh] md:w-[95vw] md:translate-x-[-50%] md:translate-y-[-50%]",
            "md:rounded-2xl md:border md:border-border/60",
            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
            "md:data-[state=closed]:slide-out-to-top-[48%] md:data-[state=open]:slide-in-from-top-[48%]",
            sizeMap[size],
            "flex flex-col overflow-hidden",
            className
          )}
        >
          {ariaTitle && (
            <DialogPrimitive.Title className="sr-only">{ariaTitle}</DialogPrimitive.Title>
          )}
          {/* Floating close (×) — visible across all overlays. */}
          <Button variant="ghost" size="icon"
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              "absolute right-3 top-3 z-[60] h-10 w-10 rounded-full",
              "bg-background/95 backdrop-blur-md shadow-lg ring-1 ring-border",
              "flex items-center justify-center text-foreground/80 hover:text-foreground",
              "hover:scale-105 active:scale-95 transition"
            )}
          >
            <X className="h-4 w-4" />
          </Button>
          {/* Mobile back chip (left) */}
          <Button variant="ghost"
            type="button"
            onClick={onClose}
            aria-label="Back"
            className={cn(
              "md:hidden absolute left-3 top-3 z-[60] h-10 px-3 rounded-full",
              "bg-background/95 backdrop-blur-md shadow-lg ring-1 ring-border",
              "flex items-center gap-1 text-sm font-medium"
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* -------------------------------------------------------------------------- */
/* Scroll container (everything below the hero scrolls together)              */
/* -------------------------------------------------------------------------- */

export function AppDialogScroll({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex-1 overflow-y-auto overscroll-contain", className)}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Hero — image + gradient + optional badge slot                              */
/* -------------------------------------------------------------------------- */

interface AppDialogHeroProps {
  image?: string | null;
  alt?: string;
  /** Slot rendered absolutely over the bottom of the hero (e.g. badges). */
  overlay?: React.ReactNode;
  /** Solid color hero when no image. */
  tone?: "primary" | "muted";
  className?: string;
}

export function AppDialogHero({
  image,
  alt,
  overlay,
  tone = "muted",
  className,
}: AppDialogHeroProps) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden",
        "h-40 sm:h-52 lg:h-60 flex-shrink-0",
        tone === "primary" ? "bg-gradient-to-br from-primary/20 to-secondary/20" : "bg-muted",
        className
      )}
    >
      {image && (
        <img
          src={image}
          alt={alt || ""}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      )}
      {/* Consistent legibility gradient */}
      <div className="gd-image-shade absolute inset-0" />
      {overlay && (
        <div className="absolute bottom-3 left-5 right-16 flex flex-wrap items-center gap-2">
          {overlay}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Header — eyebrow + title + description                                     */
/* -------------------------------------------------------------------------- */

interface AppDialogHeaderProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  /** If true, header sticks to the top of the scroll area with blur. */
  sticky?: boolean;
}

export function AppDialogHeader({
  eyebrow,
  title,
  description,
  icon,
  className,
  sticky = false,
}: AppDialogHeaderProps) {
  return (
    <div
      className={cn(
        "px-6 lg:px-8 pt-6 lg:pt-8 pb-5 space-y-3",
        sticky &&
          "sticky top-0 z-10 bg-background/85 backdrop-blur-md border-b border-border/40",
        className
      )}
    >
      {eyebrow && (
        <p className="gd-kicker text-primary">
          {eyebrow}
        </p>
      )}
      <DialogPrimitive.Title
        className={cn(
          "font-display text-2xl lg:text-3xl font-semibold leading-tight",
          "flex items-start gap-2 pr-12"
        )}
      >
        {icon && <span className="mt-1 text-primary flex-shrink-0">{icon}</span>}
        <span className="min-w-0 break-words">{title}</span>
      </DialogPrimitive.Title>
      {description && (
        <DialogPrimitive.Description className="text-sm leading-relaxed text-muted-foreground">
          {description}
        </DialogPrimitive.Description>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Body — standard padded content area                                        */
/* -------------------------------------------------------------------------- */

export function AppDialogBody({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("px-6 lg:px-8 pb-6 lg:pb-8 space-y-6", className)}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer — sticky bottom action bar                                          */
/* -------------------------------------------------------------------------- */

export function AppDialogFooter({
  className,
  children,
  align = "end",
}: {
  className?: string;
  children: React.ReactNode;
  align?: "start" | "end" | "between";
}) {
  return (
    <div
      className={cn(
        "sticky bottom-0 z-10 px-6 lg:px-8 py-4",
        "bg-background/95 backdrop-blur-md border-t border-border/60",
        "flex flex-wrap items-center gap-2",
        align === "end" && "justify-end",
        align === "start" && "justify-start",
        align === "between" && "justify-between",
        className
      )}
    >
      {children}
    </div>
  );
}
